import i18n from '../i18n'
import { API_BASE_URL, endpoints } from './endpoints'

export type User = {
  id: number
  name: string
  username: string
  first_name: string
  last_name: string
  cellphone: string | null
  email: string
  allow_delivery_notifications?: boolean
  allow_email_notifications: boolean
  allow_push_notifications: boolean
  allow_sms_notifications: boolean
  allow_whatsapp_notifications: boolean
  preferred_language: string
  is_superuser: boolean
  is_active: boolean
  last_login: string | null
}

type PersistedAuth = {
  accessToken: string | null
  refreshToken: string | null
  user: User | null
}

const STORAGE_KEY = 'auth'

let accessToken: string | null = null
let refreshToken: string | null = null
let currentUser: User | null = null
let refreshPromise: Promise<string | null> | null = null

const listeners = new Set<() => void>()

function notify() {
  for (const listener of listeners) listener()
}

function saveStorage() {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ accessToken, refreshToken, user: currentUser }))
  } catch {
    // Authentication remains available in memory when storage is unavailable.
  }
}

function loadStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return
    const data = JSON.parse(raw) as PersistedAuth
    accessToken = data.accessToken ?? null
    refreshToken = data.refreshToken ?? null
    currentUser = data.user ?? null
  } catch {
    // Ignore invalid or unavailable browser storage.
  }
}

loadStorage()

export function mapPreferredLanguage(code: string): string {
  switch ((code || '').toLowerCase()) {
    case 'pt':
    case 'pt-br':
      return 'pt-BR'
    case 'en':
      return 'en'
    case 'es':
      return 'es'
    default:
      return 'pt-BR'
  }
}

try {
  const language = mapPreferredLanguage((currentUser as User | null)?.preferred_language ?? 'pt-BR')
  if (i18n.language !== language) void i18n.changeLanguage(language)
} catch {
  // Keep the configured default language when initialization fails.
}

export function subscribeAuth(listener: () => void) {
  listeners.add(listener)
  return () => { listeners.delete(listener) }
}

export function setAccessToken(token: string | null) {
  accessToken = token
  saveStorage()
}

export function setRefreshToken(token: string | null) {
  refreshToken = token
  saveStorage()
}

export function setUser(user: User | null) {
  currentUser = user
  saveStorage()
  notify()
}

export function getUser() {
  return currentUser
}

export function getAccessToken() {
  return accessToken
}

export function getRefreshToken() {
  return refreshToken
}

export function isAuthenticated() {
  return Boolean(accessToken)
}

function loginErrorMessage(rawMessage: string | null) {
  const normalized = rawMessage?.trim().toLowerCase().replace(/[.!]+$/, '')
  if (normalized === 'invalid platform credentials'
    || normalized === 'invalid credentials'
    || normalized === 'no active account found with the given credentials') {
    return i18n.t('errors:invalid_credentials')
  }
  return rawMessage?.trim() || i18n.t('errors:invalid_credentials')
}

export async function login(username: string, password: string) {
  const response = await fetch(`${API_BASE_URL}${endpoints.auth.token()}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept-Language': i18n.language || 'pt-BR',
    },
    body: JSON.stringify({ username, password }),
  })
  if (!response.ok) {
    let rawMessage: string | null = null
    try {
      const payload = await response.json() as { detail?: unknown; message?: unknown }
      if (typeof payload.detail === 'string') rawMessage = payload.detail
      else if (typeof payload.message === 'string') rawMessage = payload.message
    } catch {
      // Some authentication failures have no JSON response body.
    }
    throw new Error(loginErrorMessage(rawMessage))
  }

  const data = await response.json()
  setAccessToken(data.access ?? null)
  setRefreshToken(data.refresh ?? null)
  const user = (data.user ?? null) as User | null
  setUser(user)
  if (user?.preferred_language) void i18n.changeLanguage(mapPreferredLanguage(user.preferred_language))
}

export function logout() {
  accessToken = null
  refreshToken = null
  currentUser = null
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch {
    // The in-memory session is cleared even when storage is unavailable.
  }
  notify()
}

export async function refreshAccessToken(): Promise<string | null> {
  if (!refreshToken) return null
  if (!refreshPromise) {
    refreshPromise = (async () => {
      try {
        const response = await fetch(`${API_BASE_URL}${endpoints.auth.refresh()}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh: refreshToken }),
        })
        if (!response.ok) return null
        const data = await response.json()
        const token = data.access as string | undefined
        setAccessToken(token ?? null)
        return token ?? null
      } catch {
        return null
      } finally {
        setTimeout(() => { refreshPromise = null }, 0)
      }
    })()
  }
  return refreshPromise
}
